import React, { useEffect, useState, useMemo } from 'react';
import './PortfolioModal.css';
import { getOptionsChain } from '../api/options';

const fmtPrice = (v) => {
  if (v == null || isNaN(v)) return '—';
  return 'R$\u202F' + Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
const fmtHealthValue = (v) => {
  if (v == null || v === '') return '—';
  if (v === Infinity || v === '∞') return '∞';
  const number = Number(v);
  return Number.isFinite(number) ? number.toFixed(2).replace('.', ',') : String(v);
};

const STRATEGY_COLORS = {
  'RSI(2) Connors':      { bg: 'rgba(38,166,154,0.10)',  border: 'rgba(38,166,154,0.35)', color: '#26a69a' },
  'Kairi + Confluência': { bg: 'rgba(129,140,248,0.10)', border: 'rgba(129,140,248,0.35)', color: '#818cf8' },
  'Scanner B3':          { bg: 'rgba(250,204,21,0.10)',  border: 'rgba(250,204,21,0.30)',  color: '#facc15' },
  'Reversão na Tendência': { bg: 'rgba(101,182,231,0.10)', border: 'rgba(101,182,231,0.35)', color: '#65b6e7' },
  'Análise Setorial':    { bg: 'rgba(167,139,250,0.10)', border: 'rgba(167,139,250,0.35)', color: '#a78bfa' },
  'Manual':              { bg: 'rgba(255,255,255,0.06)', border: 'rgba(255,255,255,0.12)', color: '#9ba3b8' },
};

export default function PortfolioModal({
  ticker,
  name,
  strategy    = 'Desconhecida',
  refs        = [],
  defaultEntryPrice  = '',
  suggestedStop      = null,
  suggestedStopLabel = '',
  suggestedTarget    = null,
  suggestedTargetLabel = '',
  pendingOrder = false,
  enforceMinRiskReward = false,
  requiresOverride = false,
  strategyHealth = null,
  onClose,
  onSave,
}) {
  const initEntry = defaultEntryPrice ? Number(defaultEntryPrice).toFixed(2) : '';

  const [form, setForm] = useState({
    entry_price:  initEntry,
    quantity:     100,
    stop_price:   suggestedStop   ? Number(suggestedStop).toFixed(2)   : '',
    target_price: suggestedTarget ? Number(suggestedTarget).toFixed(2) : '',
    notes:        '',
  });
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState(null);
  const [overrideConfirm, setOverrideConfirm] = useState(false);
  const [activeTab, setActiveTab] = useState('entrada');
  const [optionsState, setOptionsState] = useState({ loading: false, loaded: false, error: null, data: null });

  useEffect(() => {
    if (activeTab !== 'opcoes' || !ticker) return;
    let active = true;
    setOptionsState(current => ({ ...current, loading: true, error: null }));
    getOptionsChain(ticker, 'entrada_call')
      .then(data => {
        if (active) setOptionsState({ loading: false, loaded: true, error: null, data });
      })
      .catch(err => {
        if (active) setOptionsState({
          loading: false,
          loaded: true,
          error: err?.response?.data?.error || err?.message || 'Não foi possível carregar as opções.',
          data: null,
        });
      });
    return () => { active = false; };
  }, [activeTab, ticker]);

  const stratColor = STRATEGY_COLORS[strategy] || {
    bg: 'rgba(255,255,255,0.06)', border: 'rgba(255,255,255,0.12)', color: '#9ba3b8',
  };

  const rr = useMemo(() => {
    const e = parseFloat(form.entry_price);
    const s = parseFloat(form.stop_price);
    const t = parseFloat(form.target_price);
    if (!e || !s || !t || e === s || isNaN(e) || isNaN(s) || isNaN(t)) return null;
    const risk   = Math.abs(e - s);
    const reward = Math.abs(t - e);
    return {
      ratio:     reward / risk,
      riskPct:   Math.abs((e - s) / e * 100),
      rewardPct: Math.abs((t - e) / e * 100),
    };
  }, [form.entry_price, form.stop_price, form.target_price]);
  const riskRewardBlocked = enforceMinRiskReward && rr && rr.ratio < 1;

  const handleSave = async () => {
    const entry = parseFloat(form.entry_price);
    const quantity = parseInt(form.quantity, 10);
    const stop = parseFloat(form.stop_price);
    const target = parseFloat(form.target_price);
    if (saving) return;
    if (!(entry > 0) || !(quantity > 0)) {
      setError('Informe preço de entrada e quantidade válidos.');
      return;
    }
    if (!(stop > 0) || stop >= entry) {
      setError('Informe um stop de proteção abaixo do preço de entrada.');
      return;
    }
    if (!(target > entry)) {
      setError('Informe um alvo acima do preço de entrada.');
      return;
    }
    if (enforceMinRiskReward && (!rr || rr.ratio < 1)) {
      setError('R/R < 1 — não operar.');
      return;
    }
    if (requiresOverride && !overrideConfirm) {
      setOverrideConfirm(true);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSave({
        ticker,
        name:         name || ticker,
        entry_price:  entry,
        quantity,
        stop_price:   stop,
        target_price: target,
        notes:        form.notes || null,
        strategy,
         ...(requiresOverride ? { override: true } : {}),
      });
    } catch (err) {
      const apiMessage = err?.response?.data?.error || err?.data?.error || '';
      setError(/R\/R < 1|sem vantagem/i.test(apiMessage)
        ? apiMessage
        : 'Erro ao salvar. Tente novamente.');
      setSaving(false);
    }
  };

  return (
    <div className="pm-overlay" onClick={onClose}>
      <div className="pm-modal" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="pm-header">
          <div className="pm-header-left">
            <span className="pm-ticker">{ticker}</span>
            {name && name !== ticker && <span className="pm-name">{name}</span>}
          </div>
          <div className="pm-header-right">
            <span className="pm-strategy-badge" style={{
              background: stratColor.bg,
              border: `1px solid ${stratColor.border}`,
              color: stratColor.color,
            }}>
              {strategy}
            </span>
            <button className="pm-close" onClick={onClose}>✕</button>
          </div>
        </div>

        <div className="pm-tabs" role="tablist" aria-label="Detalhes da entrada">
          <button type="button" role="tab" aria-selected={activeTab === 'entrada'} className={activeTab === 'entrada' ? 'active' : ''} onClick={() => setActiveTab('entrada')}>Entrada</button>
          <button type="button" role="tab" aria-selected={activeTab === 'opcoes'} className={activeTab === 'opcoes' ? 'active' : ''} onClick={() => setActiveTab('opcoes')}>Opções do ativo</button>
        </div>

        {activeTab === 'entrada' && refs.length > 0 && (
          <div className="pm-ref-strip" style={{ gridTemplateColumns: `repeat(${refs.length}, 1fr)` }}>
            {refs.map((r, i) => (
              <div key={i} className="pm-ref-cell">
                <span className="pm-ref-label">{r.label}</span>
                <span className={`pm-ref-val${r.highlight ? ' pm-ref-highlight' : ''}`}>{r.value}</span>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'entrada' ? <div className="pm-body">

          {/* Row 1: entrada + quantidade */}
          <div className="pm-row-2">
            <div className="pm-field">
              <label className="pm-label">{pendingOrder ? 'Preço de gatilho' : 'Preço de entrada'} <span className="pm-req">*</span></label>
              <div className="pm-input-wrap">
                <span className="pm-prefix">R$</span>
                <input
                  className="pm-input"
                  type="number"
                  step="0.01"
                  placeholder="0,00"
                  value={form.entry_price}
                  onChange={e => setForm(f => ({ ...f, entry_price: e.target.value }))}
                  readOnly={pendingOrder}
                  autoFocus
                />
              </div>
            </div>
            <div className="pm-field">
              <label className="pm-label">Quantidade</label>
              <div className="pm-input-wrap">
                <input
                  className="pm-input"
                  type="number"
                  min="1"
                  style={{ paddingLeft: 12 }}
                  value={form.quantity}
                  onChange={e => setForm(f => ({ ...f, quantity: e.target.value }))}
                />
              </div>
            </div>
          </div>

          {/* Row 2: stop + alvo */}
          <div className="pm-row-2">
            <div className="pm-field">
              <label className="pm-label pm-label-stop">Stop loss</label>
              <div className="pm-input-wrap pm-input-wrap-stop">
                <span className="pm-prefix">R$</span>
                <input
                  className="pm-input"
                  type="number"
                  step="0.01"
                  placeholder="0,00"
                  value={form.stop_price}
                  onChange={e => setForm(f => ({ ...f, stop_price: e.target.value }))}
                />
              </div>
              {suggestedStop && (
                <span
                  className="pm-hint pm-hint-stop"
                  onClick={() => setForm(f => ({ ...f, stop_price: Number(suggestedStop).toFixed(2) }))}
                >
                  ↙ {fmtPrice(suggestedStop)}{suggestedStopLabel ? ` · ${suggestedStopLabel}` : ''}
                </span>
              )}
            </div>
            <div className="pm-field">
              <label className="pm-label pm-label-target">Alvo <span className="pm-req">*</span></label>
              <div className="pm-input-wrap pm-input-wrap-target">
                <span className="pm-prefix">R$</span>
                <input
                  className="pm-input"
                  type="number"
                  step="0.01"
                  placeholder="0,00"
                  value={form.target_price}
                  onChange={e => setForm(f => ({ ...f, target_price: e.target.value }))}
                />
              </div>
              {suggestedTarget && (
                <span
                  className="pm-hint pm-hint-target"
                  onClick={() => setForm(f => ({ ...f, target_price: Number(suggestedTarget).toFixed(2) }))}
                >
                  ↗ {fmtPrice(suggestedTarget)}{suggestedTargetLabel ? ` · ${suggestedTargetLabel}` : ''}
                </span>
              )}
            </div>
          </div>

          {/* R/R display */}
          {rr && (
            <div className={`pm-rr ${rr.ratio >= 2 ? 'pm-rr-good' : rr.ratio >= 1 ? 'pm-rr-ok' : 'pm-rr-bad'}`}>
              <div className="pm-rr-left">
                <span className="pm-rr-label">Risco / Retorno</span>
                <span className="pm-rr-ratio">1 : {rr.ratio.toFixed(2)}</span>
              </div>
              <div className="pm-rr-right">
                <span className="pm-rr-item stop">
                  Risco <strong>−{rr.riskPct.toFixed(1).replace('.', ',')}%</strong>
                </span>
                <span className="pm-rr-item target">
                  Retorno <strong>+{rr.rewardPct.toFixed(1).replace('.', ',')}%</strong>
                </span>
              </div>
            </div>
          )}
          {riskRewardBlocked && (
            <div className="pm-error">R/R &lt; 1 — não operar.</div>
          )}

          {/* Observações */}
          <div className="pm-field">
            <label className="pm-label">Observações</label>
            <textarea
              className="pm-textarea"
              rows={2}
              placeholder="Motivo da entrada, nível de suporte, setup específico..."
              value={form.notes}
              onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
            />
          </div>

          {error && <div className="pm-error">{error}</div>}
        </div> : (
          <div className="pm-options-body" role="tabpanel">
            <div className="pm-options-intro">
              <strong>CALLs com delta a partir de 0,15</strong>
              <span>Até 5 contratos ordenados por aderência a delta, liquidez e spread. Leitura educativa; não envia ordens.</span>
            </div>
            {optionsState.loading && <div className="pm-options-state">Carregando cadeia e métricas…</div>}
            {optionsState.error && <div className="pm-error">{optionsState.error}</div>}
            {!optionsState.loading && !optionsState.error && optionsState.loaded && !(optionsState.data?.contracts || []).length && (
              <div className="pm-options-state">
                <strong>Nenhuma CALL passou pelos filtros atuais.</strong>
                <span>{optionsState.data?.availability === 'não_atualizado' ? 'A cadeia deste ativo ainda não foi atualizada hoje.' : 'A cadeia existe, mas nenhum contrato reuniu delta ≥ 0,15, vencimento entre 20 e 75 dias, liquidez e spread aceitável.'}</span>
              </div>
            )}
            <div className="pm-options-list">
              {(optionsState.data?.contracts || []).map((contract, index) => (
                <article className="pm-option-card" key={contract.optionSymbol || `${contract.strike}-${contract.expiration}`}>
                  <div className="pm-option-rank">#{index + 1}</div>
                  <div className="pm-option-main">
                    <strong>{contract.optionSymbol || 'CALL'}</strong>
                    <span>Vencimento {contract.expiration ? String(contract.expiration).slice(0, 10).split('-').reverse().join('/') : '—'} · {contract.dte ?? '—'} dias</span>
                  </div>
                  <div><small>Delta</small><strong>{Number.isFinite(Number(contract.delta)) ? Number(contract.delta).toFixed(2) : '—'}</strong></div>
                  <div><small>Strike</small><strong>{fmtPrice(contract.strike)}</strong></div>
                  <div><small>Preço médio</small><strong>{fmtPrice(contract.mid)}</strong></div>
                  <div><small>Breakeven</small><strong>{fmtPrice(contract.breakeven)}</strong></div>
                  <div><small>Score</small><strong>{Number.isFinite(Number(contract.score)) ? `${Number(contract.score).toFixed(0)}/100` : '—'}</strong></div>
                </article>
              ))}
            </div>
          </div>
        )}

        {/* Footer / override gate */}
        {activeTab === 'entrada' && (overrideConfirm ? (
          <div className="pm-override-confirm" role="alertdialog" aria-label="Confirmação de override">
            <p className="pm-override-message">
              Esta estratégia está reprovada no backtest atual (PF pond {strategyHealth?.weightedProfitFactorInfinite ? '∞' : fmtHealthValue(strategyHealth?.weightedProfitFactor ?? strategyHealth?.profitFactor)} · líquida {fmtHealthValue(strategyHealth?.weightedNetExpectancyPct ?? strategyHealth?.netPct ?? strategyHealth?.netReturnPct)}%). Abrir mesmo assim?
            </p>
            <div className="pm-footer pm-footer-override">
              <button className="pm-cancel" onClick={() => setOverrideConfirm(false)}>Cancelar</button>
              <button className="pm-confirm pm-confirm-override" onClick={handleSave} disabled={saving}>
                {saving ? 'Salvando...' : 'Operar por minha conta'}
              </button>
            </div>
          </div>
        ) : (
          <div className="pm-footer">
            <button className="pm-cancel" onClick={onClose}>Cancelar</button>
            <button
              className="pm-confirm"
              onClick={handleSave}
              disabled={saving || !form.entry_price}
            >
              {saving ? 'Salvando...' : pendingOrder ? 'Confirmar ordem pendente' : 'Confirmar entrada'}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
